/**
 * Print page geometry for the workspace document viewer.
 *
 * Workspace documents are printed as one fixed-aspect page raster per document
 * page (a JPEG of the source page plus a transparent OCR text layer — see
 * `services/pdf-html-converter`). A raster is laid out at the full width of the
 * printed content box and derives its height from its own aspect ratio, so the
 * content box has to be at least as tall-proportioned as the raster or every
 * document page spills a sliver onto a second sheet.
 *
 * The default 15mm/10mm content box is 190x267mm — ratio 1.4053, just short of
 * A4's own 1.4142 — which is why an A4 document used to print at two sheets per
 * page. Widening the side margins by 1mm buys ~1.1mm of vertical slack.
 * `printPageGeometryFitsPaperAspect` guards that invariant.
 */
export const PRINT_PAPER_MM = { width: 210, height: 297 } as const;

export const PRINT_PAGE_MARGIN_MM = { vertical: 15, horizontal: 11 } as const;

export const printContentBoxMm = () => ({
  width: PRINT_PAPER_MM.width - 2 * PRINT_PAGE_MARGIN_MM.horizontal,
  height: PRINT_PAPER_MM.height - 2 * PRINT_PAGE_MARGIN_MM.vertical,
});

/**
 * True when a page raster shaped like the paper itself still fits the printable
 * content box at full width — i.e. one document page prints on one sheet.
 */
export const printPageGeometryFitsPaperAspect = () => {
  const content = printContentBoxMm();
  const paperAspect = PRINT_PAPER_MM.height / PRINT_PAPER_MM.width;
  return content.width * paperAspect <= content.height;
};

/** The `@page` margin shorthand, in the order CSS expects. */
export const printPageMarginCss = () =>
  `${PRINT_PAGE_MARGIN_MM.vertical}mm ${PRINT_PAGE_MARGIN_MM.horizontal}mm`;
